import { useState, useCallback, lazy, Suspense } from 'react';
import { Search } from 'lucide-react';
import { TopBar } from '@/components/navigation/TopBar';
import { BottomNav } from '@/components/navigation/BottomNav';

import { NearbyPanel } from '@/components/places/NearbyPanel';
import { MobileBottomSheet } from '@/components/mobile/MobileBottomSheet';
import { SearchModal } from '@/components/search/SearchModal';
import { usePlaces, PlaceSummary } from '@/hooks/usePlaces';
import { usePlaceDetails } from '@/hooks/usePlaceDetails';
import { useMapStore } from '@/store/mapStore';
import { useIsMobile } from '@/hooks/use-mobile';
import { FilterSheet } from '@/components/mobile/FilterSheet';
import jewmapLogo from '@/assets/jewmap-logo-optimized.webp';

// Lazy load heavy components to reduce main-thread work
const MapView = lazy(() => import('@/components/map/MapView').then(m => ({ default: m.MapView })));
const PlaceDetail = lazy(() => import('@/components/places/PlaceDetail').then(m => ({ default: m.PlaceDetail })));

// Lightweight skeleton for map loading
const MapSkeleton = () => (
  <div className="absolute inset-0 bg-muted animate-pulse" />
);

const Index = () => {
  const isMobile = useIsMobile();
  const { selectedPlace, setSelectedPlace, setCenter, triggerClosePopup } = useMapStore();
  const [activeNav, setActiveNav] = useState<'favorites' | 'locate' | 'map' | 'add' | 'profile' | 'admin'>('map');
  const [showFilters, setShowFilters] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);

  const { data: places = [], isLoading, refetch } = usePlaces();
  const { data: placeDetails } = usePlaceDetails(selectedPlaceId);

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handlePlaceSelect = useCallback((place: PlaceSummary) => {
    triggerClosePopup();
    setSelectedPlaceId(place.id);
  }, [triggerClosePopup]);

  const handleCloseDetail = useCallback(() => {
    setSelectedPlaceId(null);
    setSelectedPlace(null);
  }, [setSelectedPlace]);

  const handleSearchPlaceSelect = useCallback((place: PlaceSummary) => {
    setCenter([place.lng, place.lat]);
    setSelectedPlaceId(place.id);
  }, [setCenter]);

  return (
    <div className="fixed inset-0 overflow-hidden bg-background">
      {/* Map Layer - Lazy loaded to reduce initial bundle */}
      <Suspense fallback={<MapSkeleton />}>
        <MapView onPlaceSelect={handlePlaceSelect} />
      </Suspense>

      {/* Desktop Layout */}
      {!isMobile && (
        <>
          {/* Top Bar */}
          <div className="absolute top-4 left-4 z-20">
            <TopBar 
              onFilterClick={() => setShowFilters(true)} 
              onSearchClick={() => setShowSearch(true)}
            />
          </div>

          {/* Nearby Panel */}
          <div className="absolute top-24 left-4 bottom-24 z-20">
            <NearbyPanel 
              places={places}
              isLoading={isLoading}
              onPlaceSelect={handlePlaceSelect} 
            />
          </div>

          {/* Place Detail Modal */}
          {placeDetails && (
            <div className="absolute inset-0 z-[110] flex items-center justify-center p-4 bg-foreground/20 backdrop-blur-sm">
              <Suspense fallback={<div className="w-full max-w-2xl h-96 bg-card rounded-xl animate-pulse" />}>
                <PlaceDetail 
                  place={placeDetails} 
                  onClose={handleCloseDetail} 
                />
              </Suspense>
            </div>
          )}
        </>
      )}

      {/* Mobile Layout */}
      {isMobile && (
        <>
          {/* Mobile Header with Logo and Search */}
          <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between gap-3">
            {/* Search Button */}
            <button 
              onClick={() => setShowSearch(true)}
              aria-label="Rechercher un lieu"
              className="glass-panel-strong p-3 rounded-full shadow-lg press-effect hover:shadow-xl transition-all duration-300"
            >
              <Search className="w-5 h-5 text-foreground" />
            </button>

            {/* Logo + Legal Links */}
            <div className="glass-panel-strong px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
              <img src={jewmapLogo} alt="JewMap" width={20} height={20} className="w-5 h-5 object-contain" />
              <span className="font-bold text-xs text-primary">JewMap</span>
              <span className="text-muted-foreground/40 text-[10px]">·</span>
              <a href="/privacy" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors underline">Privacy</a>
              <span className="text-muted-foreground/40 text-[10px]">·</span>
              <a href="/terms" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">CGU</a>
            </div>

            {/* Filter Button */}
            <button 
              onClick={() => setShowFilters(true)}
              aria-label="Ouvrir les filtres"
              className="glass-panel-strong p-3 rounded-full shadow-lg press-effect hover:shadow-xl transition-all duration-300"
            >
              <svg className="w-5 h-5 text-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="4" y1="21" x2="4" y2="14" />
                <line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" />
                <line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" />
                <line x1="9" y1="8" x2="15" y2="8" />
                <line x1="17" y1="16" x2="23" y2="16" />
              </svg>
            </button>
          </div>

          {!placeDetails && (
            <MobileBottomSheet 
              places={places}
              isLoading={isLoading}
              onPlaceSelect={handlePlaceSelect}
              onClose={handleCloseDetail}
              onRefresh={handleRefresh}
            />
          )}

          {/* Place Detail (Full Screen on Mobile) */}
          {placeDetails && (
            <div className="absolute inset-0 z-[110] bg-card animate-slide-up">
              <div className="h-full overflow-y-auto pb-safe">
                <Suspense fallback={<div className="w-full h-full bg-card animate-pulse" />}>
                  <PlaceDetail 
                    place={placeDetails} 
                    onClose={handleCloseDetail} 
                  />
                </Suspense>
              </div>
            </div>
          )}

          {/* Filter Sheet */}
          <FilterSheet 
            isOpen={showFilters} 
            onClose={() => setShowFilters(false)} 
          />
        </>
      )}

      {/* Filter Sheet for Desktop */}
      {!isMobile && (
        <FilterSheet 
          isOpen={showFilters} 
          onClose={() => setShowFilters(false)} 
        />
      )}

      {/* Bottom Navigation - fixed to viewport, hidden when PlaceDetail is open */}
      {!placeDetails && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-xl border-t border-border/30">
          <BottomNav 
            activeItem={activeNav} 
            onItemClick={setActiveNav}
            onSearchClick={() => setShowSearch(true)}
          />
          {/* Safe area spacer for iOS */}
          <div style={{ height: 'env(safe-area-inset-bottom)' }} />
        </div>
      )}

      {/* Legal Footer - Visible for Google OAuth compliance (Desktop only) */}
      {!isMobile && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 text-xs text-muted-foreground/70 bg-background/80 backdrop-blur-sm px-3 py-1 rounded-full">
          <span className="font-medium">JewMap</span>
          <span>·</span>
          <a href="/privacy" className="hover:text-foreground transition-colors underline">Politique de confidentialité</a>
          <span>·</span>
          <a href="/terms" className="hover:text-foreground transition-colors">CGU</a>
        </div>
      )}

      {/* Search Modal */}
      <SearchModal
        open={showSearch}
        onOpenChange={setShowSearch}
        onPlaceSelect={handleSearchPlaceSelect}
      />
    </div>
  );
};

export default Index;
